// Loaded only by the public /funds-for-a-risk-level/ page. Fills the "fee cost at your horizon" line under each fund from
// the household the planner has saved in this browser (same origin, same storage), using the calculators' cost-of-fees
// logic. Nothing is fetched or sent; without a saved household with years to go, the page's own placeholder text stays.
import { normaliseHousehold, type Household } from '@iht/engine';
import { loadJSON, hasKey, KEYS } from './store';
import { feesCost } from './calculators/pensions';
import { fmt } from './format';

function household(): Household | null {
  try {
    if (hasKey(KEYS.household)) return normaliseHousehold(loadJSON<unknown>(KEYS.household, null));
    if (hasKey(KEYS.legacyState)) return normaliseHousehold(loadJSON<unknown>(KEYS.legacyState, null));
  } catch { /* unreadable or absent */ }
  return null;
}

const h = household();
const you = h?.people[0];
const years = you ? Math.round(you.retireAge - you.age) : 0;
if (h && you && years >= 1) {
  const pot = h.people.reduce((s, p) => s + p.pots.reduce((a, q) => a + q.value, 0), 0);
  const contribution = h.people.reduce((s, p) => s + p.contribOwn + p.contribEmp, 0);
  const growth = you.pots[0]?.growth ?? 5;
  const cache = new Map<number, ReturnType<typeof feesCost>>();
  for (const el of document.querySelectorAll<HTMLElement>('[data-fee-ocf]')) {
    const ocf = parseFloat(el.dataset.feeOcf ?? '');
    if (!Number.isFinite(ocf)) continue;
    const r = cache.get(ocf) ?? feesCost(pot, contribution, years, ocf, growth);
    cache.set(ocf, r);
    el.textContent = `At your figures — ${fmt(pot)} in pensions today, ${fmt(contribution)} a year going in, ${years} years to retirement at ${growth}% growth — this fund's ${ocf.toFixed(2)}% charge leaves about ${fmt(r.withFees)}, ${fmt(Math.max(0, r.lost))} less than the same money at 0.2%; charges paid ${fmt(r.feesPaid)}. Fund charge only; your platform's fee comes on top.`;
    el.classList.add('fee-live');
  }
}
